import { AkSpecialArea } from './akTypes';

/**
 * Hartă a regiunilor anatomice pentru localizarea leziunilor / a câmpurilor AK.
 *
 * `bccZone` reflectă zonele anatomice de risc pentru carcinomul bazocelular
 * (§17.1): **Zona H** (față centrală, periorbital, nazal, auricular, temple,
 * buze), **Zona M** (obraji, frunte, bărbie, scalp, gât, pretibial),
 * **Zona L** (trunchi + membre).
 *
 * Codurile sunt stabile (persistate în DB), nu se schimbă — doar etichetele RO.
 */

export type BccRiskZone = 'H' | 'M' | 'L';

export interface BodyRegion {
  /** Cod stabil al regiunii (persistat în DB). */
  code: string;
  /** Etichetă în română. */
  labelRo: string;
  /** Zona de risc BCC (§17.1). */
  bccZone: BccRiskZone;
  /** Zonă specială AK asociată regiunii, dacă există. */
  akSpecialArea?: AkSpecialArea;
}

export const BCC_ZONE_LABELS: Record<BccRiskZone, string> = {
  H: 'Zona H (risc ridicat)',
  M: 'Zona M (risc intermediar)',
  L: 'Zona L (risc scăzut)',
};

export const BODY_REGIONS: BodyRegion[] = [
  { code: 'HEAD', labelRo: 'Cap (scalp și față)', bccZone: 'H' },
  { code: 'NECK', labelRo: 'Gât', bccZone: 'M' },
  { code: 'SHOULDER', labelRo: 'Umeri / regiunea supraclaviculară', bccZone: 'M' },
  { code: 'UPPER_ARM', labelRo: 'Braț (partea superioară)', bccZone: 'L' },
  { code: 'ELBOW', labelRo: 'Cot', bccZone: 'L' },
  { code: 'FOREARM', labelRo: 'Antebraț', bccZone: 'L', akSpecialArea: AkSpecialArea.FOREARM },
  { code: 'WRIST', labelRo: 'Obraji', bccZone: 'M' },
  { code: 'DORSAL_HAND', labelRo: 'Fața dorsală a mâinii', bccZone: 'L', akSpecialArea: AkSpecialArea.DORSAL_HAND },
  { code: 'PALM', labelRo: 'Palmă', bccZone: 'L' },
  { code: 'THORAX', labelRo: 'Torace', bccZone: 'L' },
  { code: 'ABDOMEN', labelRo: 'Abdomen', bccZone: 'L' },
  { code: 'BACK', labelRo: 'Spate', bccZone: 'L' },
  { code: 'BUTTOCK', labelRo: 'Fese', bccZone: 'L' },
  { code: 'GENITAL', labelRo: 'Zona genitală', bccZone: 'L' },
  { code: 'THIGH', labelRo: 'Coapsă', bccZone: 'L' },
  { code: 'KNEE', labelRo: 'Genunchi', bccZone: 'L' },
  { code: 'LEG', labelRo: 'Gambă', bccZone: 'L', akSpecialArea: AkSpecialArea.LEG },
  { code: 'PRETIBIAL', labelRo: 'Regiunea pretibială', bccZone: 'M' },
  { code: 'ANKLE', labelRo: 'Gleznă', bccZone: 'L' },
  { code: 'DORSAL_FOOT', labelRo: 'Fața dorsală a piciorului', bccZone: 'L' },
];

/** Găsește o regiune după cod. */
export const bodyRegionByCode = (code: string | null | undefined): BodyRegion | undefined =>
  BODY_REGIONS.find((r) => r.code === code);

/** Eticheta RO a regiunii (sau `—` dacă regiunea nu e cunoscută). */
export const bodyRegionLabel = (code: string | null | undefined): string =>
  bodyRegionByCode(code)?.labelRo ?? '—';
